import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Entitlements } from '@/types'
import { getEntitlements as getEntitlementsForPlan } from '@/lib/billing/plan'
import {
  DEFAULT_LOCALISATION_CONFIG,
  formatCurrencyByConfig,
  formatDateByConfig,
  formatDateTimeByConfig,
  formatNumberByConfig,
  type LocalisationConfig,
} from '@/lib/localisation/config'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ─── Currency / Numbers ─────────────────────────────────────────────────────

export function formatCurrency(amount: number, currency = 'ZAR', config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG): string {
  return formatCurrencyByConfig(amount, currency, config)
}

export function formatNumber(n: number, decimals = 2, config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG): string {
  return formatNumberByConfig(n, decimals, config)
}

export function roundUp(value: number, nearest = 0.5): number {
  return Math.ceil(value / nearest) * nearest
}

// ─── Entitlements ───────────────────────────────────────────────────────────

/**
 * Re-export of `getEntitlements` from `@/lib/billing/plan` so existing callers
 * importing from `@/lib/utils` keep working. Always normalizes the plan first
 * — never bypass this and read `PLAN_ENTITLEMENTS` directly.
 */
export function getEntitlements(plan: unknown): Entitlements {
  return getEntitlementsForPlan(plan)
}

export function canUseFeature(entitlements: Entitlements, feature: keyof Entitlements): boolean {
  const val = entitlements[feature]
  if (typeof val === 'boolean') return val
  if (typeof val === 'number') return val !== 0
  return false
}

export function isAtLimit(entitlements: Entitlements, field: keyof Entitlements, current: number): boolean {
  const limit = entitlements[field]
  if (typeof limit !== 'number') return false
  if (limit === -1) return false // unlimited
  return current >= limit
}

// ─── Date helpers ───────────────────────────────────────────────────────────

export function formatDate(date: string | Date, config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG): string {
  return formatDateByConfig(date, config)
}

export function formatDateTime(date: string | Date, config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG): string {
  return formatDateTimeByConfig(date, config)
}

export function daysFromNow(days: number): Date {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d
}

// ─── Pricing formulas (deterministic) ──────────────────────────────────────

/**
 * Correct margin pricing: price = cost / (1 - margin%)
 * NOT markup: price = cost * (1 + markup%)
 */
export function applyMargin(cost: number, marginDecimal: number): number {
  if (marginDecimal >= 1) throw new Error('Margin must be < 1 (e.g. 0.30 for 30%)')
  return cost / (1 - marginDecimal)
}

export function calcVat(exclPrice: number, vatRate: number): number {
  return exclPrice * vatRate
}

export function calcDeposit(totalIncVat: number, depositPercent: number): number {
  return totalIncVat * depositPercent
}

export function calcOverheadAllocation(
  monthlyOverhead: number,
  billableHoursPerMonth: number,
  jobHours: number
): number {
  if (billableHoursPerMonth === 0) return 0
  return (monthlyOverhead / billableHoursPerMonth) * jobHours
}

export function calcTrueLabourRate(baseRate: number, onCostFactor: number): number {
  return baseRate * onCostFactor
}

// ─── Quote totals (source of truth) ─────────────────────────────────────────

export interface QuoteTotals {
  subtotalMaterials: number
  subtotalLabour: number
  subtotalOverhead: number
  transport: number
  jobCost: number
  grossPrice: number
  vatAmount: number
  totalIncVat: number
  depositAmount: number
  balanceDue: number
}

export function calculateQuoteTotals(
  lineItems: Array<{ category: string; total: number }>,
  labourLines: Array<{ total: number }>,
  transportCost: number,
  overheadAllocation: number,
  marginPercent: number,
  vatRate: number,
  depositPercent: number
): QuoteTotals {
  const subtotalMaterials = lineItems
    .filter(li => li.category === 'material')
    .reduce((sum, li) => sum + li.total, 0)

  const subtotalOtherItems = lineItems
    .filter(li => li.category !== 'material' && li.category !== 'labour')
    .reduce((sum, li) => sum + li.total, 0)

  const subtotalLabour = labourLines.reduce((sum, ll) => sum + ll.total, 0) +
    lineItems.filter(li => li.category === 'labour').reduce((sum, li) => sum + li.total, 0)

  const subtotalOverhead = overheadAllocation

  const jobCost = subtotalMaterials + subtotalLabour + subtotalOtherItems + subtotalOverhead + transportCost

  const grossPrice = applyMargin(jobCost, marginPercent)
  const vatAmount = calcVat(grossPrice, vatRate)
  const totalIncVat = grossPrice + vatAmount
  const depositAmount = calcDeposit(totalIncVat, depositPercent)
  const balanceDue = totalIncVat - depositAmount

  return {
    subtotalMaterials,
    subtotalLabour,
    subtotalOverhead,
    transport: transportCost,
    jobCost,
    grossPrice,
    vatAmount,
    totalIncVat,
    depositAmount,
    balanceDue,
  }
}

// ─── Device fingerprint ─────────────────────────────────────────────────────

export async function generateDeviceFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0,
  ].join('|')

  // Use SubtleCrypto for a stable hash
  const encoder = new TextEncoder()
  const data = encoder.encode(components)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

// ─── Misc ───────────────────────────────────────────────────────────────────

export function generateId(): string {
  return crypto.randomUUID()
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function formatQuoteNumber(jobNumber: string | null | undefined, versionNumber: number): string {
  const base = (jobNumber ?? 'QUO-0000').replace(/^JOB-/, 'QUO-')
  return `${base}-v${versionNumber}`
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

export const TRADE_LABELS: Record<string, string> = {
  painting: 'Painting',
  concrete: 'Concrete',
  tiling: 'Tiling',
  roofing: 'Roofing',
  paving: 'Paving',
  plastering: 'Plastering',
  waterproofing: 'Waterproofing',
  other: 'Other',
}

export const JOB_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  quoted: 'Quoted',
  accepted: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
}

export const JOB_STATUS_COLOURS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  quoted: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-200 text-emerald-900',
  cancelled: 'bg-red-100 text-red-700',
  on_hold: 'bg-orange-100 text-orange-700',
}

export const QUOTE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  accepted: 'Confirmed',
  declined: 'Declined',
  expired: 'Expired',
  revised: 'Revised',
  changes_requested: 'Changes Requested',
}

export const QUOTE_STATUS_COLOURS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-purple-100 text-purple-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  expired: 'bg-orange-100 text-orange-700',
  revised: 'bg-amber-100 text-amber-700',
  changes_requested: 'bg-orange-100 text-orange-700',
}

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  part_paid: 'Part Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
}

export const INVOICE_STATUS_COLOURS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  part_paid: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  void: 'bg-zinc-200 text-zinc-700',
}
